import { App, AppType, GetSpacesSortField, Space, SpaceConfig } from '@flatfile/api-internal/api'
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
  Input,
  Label,
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  SimpleSearchBar,
  Tabs,
  TabsList,
  TabsTrigger,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@flatfile/baseline'
import { Dispatch, RefObject, SetStateAction, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { CreateNewSpace } from '../../components/CreateNewSpace/CreateNewSpace'
import { ResourceList } from '../../components/ResourceList/ResourceList'
import { ResourceListContextProvider } from '../../components/ResourceList/ResourceListContext'
import { ListPagination, ResourceHeaderComponent, ResourceRowComponent } from '../../components/ResourceList/types'
import { ResourceListBulkActions } from '../../components/ResourceListBulkActions/ResourceListBulkActions'
import { CustomCopyButton } from '../../components/Shared/CopyToClipboard'
import TrialBanner from '../../components/Shared/TrialBanner'
import { UserInfoGuest, badgeColors } from '../../components/Shared/UserInfoGuest'
import { StatsGrid } from '../../components/StatsHeader/StatsHeader'
import { useDebounce } from '../../hooks/useDebounce'
import { useDynamicHeight } from '../../hooks/useDynamicHeight'
import { useActiveEnvironment } from '../../hooks/useEnvironments'
import { useIdentity } from '../../hooks/useIdentity'
import { useCollection } from '../../resources/hooks/useCollection'
import { useFilteredCollection } from '../../resources/hooks/useCollectionIds'
import useResource from '../../resources/hooks/useResource'
import useResourceList from '../../resources/hooks/useResourceList'
import {
  useArchiveResource,
  useBulkUpdateResource,
  useDeleteResource,
  useUpdateResource,
} from '../../resources/hooks/useResourceMutation'
import { formatDate, formatTime, getFormattedBytes, getFormattedNumberToMillions } from '../../utils/format'
import { logger } from '../../utils/logging'
import { spacesUrl } from '../../utils/pageUtils'
import { CustomErrorComponent } from '../../components/Shared/CustomErrorComponent'
import { useCustomAppPage } from './hooks/useCustomAppPage'
import { StatsUI, convertStatsResponseToUI } from './utilities/customAppHelpers'

type RequestParams = {
  search?: string
  app: string
  sortField?: GetSpacesSortField
  sortDirection?: 'asc' | 'desc'
  archived?: boolean
  isAppTemplate?: boolean
}

const SortButton = memo(
  ({
    field,
    setRequestParams,
    requestParams,
  }: {
    field: GetSpacesSortField
    requestParams: RequestParams
    setRequestParams: Dispatch<SetStateAction<RequestParams>>
  }) => {
    const handleClick = useCallback(() => {
      setRequestParams((prev) => {
        if (prev?.sortField !== field) {
          return { ...prev, sortField: field, sortDirection: 'asc' }
        }
        if (prev.sortDirection === 'asc') {
          return { ...prev, sortDirection: 'desc' }
        }
        return { ...prev, sortField: undefined, sortDirection: undefined }
      })
    }, [field, setRequestParams])

    const iconName = useMemo(() => {
      if (requestParams?.sortField === field) {
        return requestParams?.sortDirection === 'asc' ? 'chevron-top' : 'chevron-bottom'
      }
      return 'chevron-grabber-vertical'
    }, [field, requestParams?.sortField, requestParams?.sortDirection])

    return (
      <button
        type='button'
        className='p-0 bg-transparent border-none cursor-pointer flex items-center justify-center'
        onClick={handleClick}
      >
        <Icon name={iconName} size={12} className='inline-flex' />
      </button>
    )
  }
)
SortButton.displayName = 'SortButton'

const CustomAppTableHeaderGenerator = ({
  requestParams,
  setRequestParams,
  columnWidths,
  tableRef,
}: {
  requestParams: RequestParams
  setRequestParams: Dispatch<SetStateAction<RequestParams>>
  columnWidths: { [key: string]: number }
  tableRef: RefObject<HTMLTableSectionElement>
}) => {
  const CustomAppTableHeader: ResourceHeaderComponent = ({ toggleAll, selections, ids }) => {
    const { namespace } = useParams()
    const { id = '' } = useParams()
    const currentAppPredicate = useCallback((app: App) => app.id === id || app.namespace === namespace, [id, namespace])
    const currentAppId = useFilteredCollection('APPS', currentAppPredicate)
    const currentApp = useResource('APPS', currentAppId[0])

    return (
      <thead ref={tableRef} className='border-b border-gray-200'>
        <tr>
          <th style={{ width: `${columnWidths.column0}%` }} className='px-3 py-2 text-left align-middle h-[42px]'>
            <div className='flex items-center justify-center h-full w-full'>
              <Checkbox
                onCheckedChange={toggleAll || (() => null)}
                checked={selections ? selections.length === ids?.length : false}
                className='size-4 !self-center'
                aria-label='Select all'
              />
            </div>
          </th>
          <th style={{ width: `${columnWidths.column1}%` }} className='px-3 py-2 text-left align-middle h-[42px]'>
            <Text variant='subheadline' emphasized className='text-muted flex items-center h-full'>
              {currentApp?.entity} Id
            </Text>
          </th>
          <th style={{ width: `${columnWidths.column2}%` }} className='px-3 py-2 text-left align-middle h-[42px]'>
            <Text variant='subheadline' emphasized className='text-muted flex items-center'>
              Name{' '}
              <span className='inline-flex items-center ml-1'>
                <SortButton
                  field={GetSpacesSortField.Name}
                  requestParams={requestParams}
                  setRequestParams={setRequestParams}
                ></SortButton>
              </span>
            </Text>
          </th>
          <th style={{ width: `${columnWidths.column3}%` }} className='px-3 py-2 text-left align-middle h-[42px]'>
            <Text variant='subheadline' emphasized className='text-muted flex items-center h-full'>
              Label(s)
            </Text>
          </th>
          <th style={{ width: `${columnWidths.column4}%` }} className='px-3 py-2 text-left align-middle h-[42px]'>
            <Text variant='subheadline' emphasized className='text-muted flex items-center'>
              Created by{' '}
              <span className='inline-flex items-center ml-1'>
                <SortButton
                  field={GetSpacesSortField.CreatedByUserName}
                  requestParams={requestParams}
                  setRequestParams={setRequestParams}
                ></SortButton>
              </span>
            </Text>
          </th>
          <th style={{ width: `${columnWidths.column5}%` }} className='px-3 py-2 text-left align-middle h-[42px]'>
            <Text variant='subheadline' emphasized className='text-muted flex items-center'>
              Created at{' '}
              <span className='inline-flex items-center ml-1'>
                <SortButton
                  field={GetSpacesSortField.CreatedAt}
                  requestParams={requestParams}
                  setRequestParams={setRequestParams}
                ></SortButton>
              </span>
            </Text>
          </th>
          <th style={{ width: `${columnWidths.column6}%` }} className='px-3 py-2 text-left align-middle h-[42px]'>
            <Text variant='subheadline' emphasized className='text-muted flex items-center'>
              Last Activity
              <span className='inline-flex items-center ml-1'>
                <SortButton
                  field='lastActivityAt'
                  requestParams={requestParams}
                  setRequestParams={setRequestParams}
                ></SortButton>
              </span>
            </Text>
          </th>
          <th style={{ width: `${columnWidths.column7}%` }} className='px-3 py-2 text-left align-middle h-[42px]'>
            <Text variant='subheadline' emphasized className='text-muted flex items-center h-full'>
              # Files
            </Text>
          </th>
          <th style={{ width: `${columnWidths.column8}%` }} className='px-3 py-2 text-left align-middle h-[42px]'>
            <Text variant='subheadline' emphasized className='text-muted flex items-center h-full'>
              Data
            </Text>
          </th>
          <th
            style={{ width: `${columnWidths.column9}%`, minWidth: '80px' }}
            className='px-3 py-2 text-left align-middle h-[42px]'
          >
            <Text variant='subheadline' emphasized className='text-muted flex items-center h-full'>
              PDV
            </Text>
          </th>
          <th style={{ width: `${columnWidths.column10}%` }} className='px-3 py-2 text-left align-middle h-[42px]'>
            <Text variant='subheadline' emphasized className='text-muted flex items-center h-full'>
              Guests
            </Text>
          </th>
          <th style={{ width: `${columnWidths.column11}%` }} className='px-3 py-2 text-left align-middle h-[42px]'>
            <Text variant='subheadline' emphasized className='text-muted flex items-center h-full'></Text>
          </th>
        </tr>
      </thead>
    )
  }
  return CustomAppTableHeader
}

const Guests = ({ spaceId }: { spaceId: string }) => {
  const guests = useResourceList('GUESTS', { spaceId })
  const filteredGuests = useMemo(
    () => guests.response.data.filter((guest) => guest.name !== 'Anonymous Guest'),
    [guests]
  )
  const guestNames = filteredGuests.map((guest) => guest.name).join(', ')
  if (filteredGuests.length > 0) {
    // Render with Tooltip if there are guests
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className='inline-block cursor-pointer'>
            <Text className='text-sm font-normal'>
              {filteredGuests.length > 1 ? `${filteredGuests.length} guests` : '1 guest'}
            </Text>
          </span>
        </TooltipTrigger>
        <TooltipContent className='flex items-center px-2 py-1'>
          <Text variant='body' className='text-sm'>
            Guest Names: {guestNames}
          </Text>
        </TooltipContent>
      </Tooltip>
    )
  }
  // Render without Tooltip if there are no guests
  return <Text className='text-sm text-muted'>-</Text>
}

const CreatedBy = ({ space, currentApp }: { space: Space; currentApp: App | null }) => {
  return (
    <div className='flex flex-col gap-1'>
      {currentApp?.namespace === 'projects' ? (
        <Text className='text-xs text-gray-700 truncate'>
          Created by {space.createdByUserName ?? 'Anonymous Guest'}
        </Text>
      ) : space.metadata?.userInfo?.name ? (
        <UserInfoGuest space={space}></UserInfoGuest>
      ) : (
        <Text className='text-sm font-normal truncate'>{space.createdByUserName || 'Anonymous Guest'}</Text>
      )}
    </div>
  )
}

const CustomAppRow: ResourceRowComponent = memo(({ resourceId, selected, onSelect, onRowActionSuccess }) => {
  const { identity, hasPermission } = useIdentity()
  const { environment } = useActiveEnvironment()
  const { id = '', namespace } = useParams()
  const currentAppPredicate = useCallback((app: App) => app.id === id || app.namespace === namespace, [id, namespace])
  const currentAppId = useFilteredCollection('APPS', currentAppPredicate)
  const currentApp = useResource('APPS', currentAppId[0])
  const isDefault = identity.account.defaultAppId === currentApp?.id

  const [deleteSpace] = useDeleteResource('SPACES')
  const [updateSpace, updatingSpace] = useUpdateResource('SPACES')
  const [unarchiveSpace] = useBulkUpdateResource('SPACES')
  const [archiveSpace] = useArchiveResource('SPACES')

  const [openedDeleteSpace, setOpenedDeleteSpace] = useState(false)
  const [openedUpdateSpace, setOpenedUpdateSpace] = useState(false)
  const [openedMoveSpace, setOpenedMoveSpace] = useState(false)
  const [openedPinSpace, setOpenedPinSpace] = useState(false)

  const [updateError, setUpdateError] = useState('')

  const apps = useResourceList('APPS').response.data
  const otherApps = useMemo(
    () => apps.filter((a: any) => ![currentApp?.name!, 'Mapping'].includes(a.name)),
    [apps, currentApp]
  )

  const [selectedApp, setSelectedApp] = useState<string | null>(otherApps.length > 0 ? otherApps[0].name : null)

  const space = useResource('SPACES', resourceId)

  if (!space) {
    return <></>
  }
  const spaceRootUrl = spacesUrl(
    space?.id ?? '',
    identity.token,
    identity.account.vanityDomainSpaces,
    identity.account.subdomain
  )

  const handleDeleteClick = async () => {
    try {
      // Attempt to call deleteSpace
      await deleteSpace(space.id)
      setOpenedDeleteSpace(false)
      onRowActionSuccess && onRowActionSuccess('DELETE')
    } catch (error) {
      // Handle errors if deleteSpace fails
      logger.error('Error deleting space', { error })
    }
  }

  const handleMoveClick = async () => {
    const targetApp = otherApps.find((a) => a.name === selectedApp)
    try {
      await updateSpace(space.id, { appId: targetApp?.id! })
      setOpenedMoveSpace(false)
      onRowActionSuccess && onRowActionSuccess('MOVE')
    } catch (error) {
      logger.error('Failed to move space', { error })
    }
  }

  const handlePinClick = async () => {
    try {
      await updateSpace(space.id, { appId: currentApp?.id })
      setOpenedMoveSpace(false)
      onRowActionSuccess && onRowActionSuccess('PIN')
    } catch (error) {
      logger.error('Failed to pin space', { error })
    }
  }

  type UpdateSpaceForm = {
    name: string
    namespace: string
  }

  // Custom form state implementation replacing Mantine useForm
  const [formValues, setFormValues] = useState<UpdateSpaceForm>({
    name: space?.name || '',
    namespace: space?.namespace || '',
  })
  const [formErrors, setFormErrors] = useState<{ name: string | null }>({
    name: null,
  })

  const setFieldValue = (field: keyof UpdateSpaceForm, value: string) => {
    setFormValues((prev) => ({ ...prev, [field]: value }))
  }

  const validate = (): boolean => {
    const errors = { name: null as string | null }

    if (formValues.name.trim().length === 0) {
      errors.name = 'Name is required.'
      setFormErrors(errors)
      return false
    }

    setFormErrors({ name: null })
    return true
  }

  const handleUpdateSpace = async (e: React.FormEvent) => {
    e.preventDefault()

    if (updatingSpace || !validate()) return

    const flatFileConfig: SpaceConfig = {
      name: formValues.name,
    }

    try {
      await updateSpace(space.id, flatFileConfig)
      setOpenedUpdateSpace(false)
    } catch (error) {
      logger.error('Failed to update space', { error })
      setUpdateError(`Failed to update ${currentApp?.entity}. Please try again.`)
    }
  }

  const handleArchive = async () => {
    try {
      if (space.archivedAt)
        //to-do: replace this when api package has unarchive endpoint
        await unarchiveSpace({ spaceIds: [space.id], environmentId: environment.id, archive: false })
      else {
        await archiveSpace(space.id)
      }
      onRowActionSuccess && onRowActionSuccess('ARCHIVE')
    } catch (error) {
      logger.error('Failed to update space', { error })
      setUpdateError(`Failed to update ${currentApp?.entity}. Please try again.`)
    }
  }

  return (
    <tr className='hover:bg-gray-50 rounded-md overflow-hidden p-1'>
      <td className='px-3 py-2 text-left align-middle border-b border-gray-200 rounded-l-md'>
        <div className='flex items-center justify-center h-full'>
          <Checkbox
            checked={selected}
            onCheckedChange={() => onSelect && onSelect(space ? space.id : '')}
            className='size-4'
          />
        </div>
      </td>
      <td className='px-3 py-2 text-left align-middle border-b border-gray-200'>
        <div className='flex items-center h-full'>
          <CustomCopyButton value={space.id} />
        </div>
      </td>
      <td className='px-3 py-2 text-left align-middle border-b border-gray-200'>
        <div className='flex items-center h-full'>
          {space.expiredAt ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className='inline-block cursor-pointer'>
                  <Text variant='body' className='text-gray-500 text-sm'>
                    Expired
                  </Text>
                </span>
              </TooltipTrigger>
              <TooltipContent className='flex items-center px-2 py-1'>
                <Text variant='body' className='text-sm'>
                  Expired session
                </Text>
              </TooltipContent>
            </Tooltip>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={() => window.open(spaceRootUrl, '_blank', 'noopener,noreferrer')}
                  variant='ghost'
                  className='text-primary px-0 py-0 h-auto'
                >
                  <span className='inline-flex items-center justify-center align-middle'>
                    <Text variant='body' emphasized className='text-sm'>
                      {space.name}
                    </Text>
                    <Icon
                      name='circle-info'
                      className='ml-1 text-gray-500'
                      size={12}
                      style={{ display: 'inline-flex', verticalAlign: 'middle' }}
                    />
                  </span>
                </Button>
              </TooltipTrigger>
              <TooltipContent className='flex items-center px-2 py-1'>
                <Text variant='body' className='text-sm'>
                  {!space.appId ? 'Open this unlinked space in new tab' : 'Open in new tab'}
                </Text>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </td>
      <td className='px-3 py-2 text-left align-middle border-b border-gray-200'>
        <div className='flex items-center flex-wrap h-full'>
          <Text variant='body'>
            {space.labels?.map((label, index) => (
              <div
                key={index}
                className='inline-flex items-center border px-1.5 py-0.5 text-xxs rounded-sm mr-1 truncate align-middle my-auto'
                style={{
                  backgroundColor: `var(--tokens-light-color-${badgeColors[index % badgeColors.length]}-3)`,
                  color: `var(--tokens-light-color-${badgeColors[index % badgeColors.length]}-9)`,
                  borderColor: `var(--tokens-light-color-${badgeColors[index % badgeColors.length]}-6)`,
                  verticalAlign: 'middle',
                  lineHeight: 1,
                }}
              >
                {label}
              </div>
            ))}
            {isDefault && !space.appId && (
              <Badge
                className='mr-1 rounded-sm truncate align-middle my-auto items-center border px-1.5 py-0.5 text-base-white !text-xxs'
                variant='destructive'
              >
                Unlinked Space
              </Badge>
            )}
          </Text>
        </div>
      </td>
      <td className='px-3 py-2 text-left align-middle border-b border-gray-200'>
        <div className='flex items-center h-full'>
          <CreatedBy space={space} currentApp={currentApp} />
        </div>
      </td>
      <td className='px-3 py-2 text-left align-middle border-b border-gray-200 whitespace-nowrap'>
        <div className='flex items-center h-full'>
          <Text variant='body' className='text-sm'>
            {formatDate(space.createdAt)} {formatTime(space.createdAt)}
          </Text>
        </div>
      </td>
      <td className='px-3 py-2 text-left align-middle border-b border-gray-200 whitespace-nowrap'>
        <div className='flex items-center h-full'>
          <Text variant='body' className='text-sm'>
            {space.lastActivityAt ? (
              <>
                {formatDate(space.lastActivityAt)} {formatTime(space.lastActivityAt)}
              </>
            ) : (
              'No activity'
            )}
          </Text>
        </div>
      </td>
      <td className='px-3 py-2 text-left align-middle border-b border-gray-200'>
        <div className='flex items-center h-full'>
          <Text variant='body' className='text-sm'>
            {space.filesCount ?? 0}
          </Text>
        </div>
      </td>

      <td className='px-3 py-2 text-left align-middle border-b border-gray-200'>
        <div className='flex items-center h-full'>
          <Text variant='body' className='text-sm font-normal'>
            {getFormattedBytes(space.allFilesSize * 1024)}
          </Text>
        </div>
      </td>
      <td className='px-3 py-2 text-left align-middle border-b border-gray-200' style={{ minWidth: '60px' }}>
        <div className='flex items-center h-full'>
          <Text variant='body' className='text-sm font-normal'>
            {getFormattedNumberToMillions(space.pdvCount * 1000)} PDV
          </Text>
        </div>
      </td>
      <td className='px-3 py-2 text-left align-middle border-b border-gray-200'>
        <div className='flex items-center h-full'>
          <Guests spaceId={space.id} />
        </div>
      </td>
      <td className='px-3 py-2 text-left align-middle border-b border-gray-200 rounded-r-md'>
        <div className='flex items-center justify-center h-full'>
          {hasPermission({ resource: environment, permissionKey: 'space:delete' }) && currentApp && (
            <Dialog open={openedDeleteSpace} onOpenChange={setOpenedDeleteSpace}>
              <DialogContent className='sm:max-w-lg'>
                <DialogHeader>
                  <DialogTitle>Proceed with deleting?</DialogTitle>
                </DialogHeader>
                <div>
                  <Text className='text-sm'>
                    You are about to permanently delete this {currentApp.entity}. This action cannot be reversed.
                    <b> Are you sure you want to proceed in deleting {space.name}?</b>
                  </Text>
                  <div className='h-6'></div>
                  <Button variant='destructive' className='w-full' onClick={handleDeleteClick}>
                    Yes, Delete Permanently
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}

          <Dialog open={openedUpdateSpace} onOpenChange={setOpenedUpdateSpace}>
            <DialogContent className='sm:max-w-md'>
              <DialogHeader>
                <DialogTitle>Edit {space.name}</DialogTitle>
              </DialogHeader>
              {updateError && (
                <Alert variant='destructive' className='mb-4'>
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription className='text-sm'>{updateError}</AlertDescription>
                </Alert>
              )}
              <form onSubmit={handleUpdateSpace}>
                <div className='space-y-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='name'>Name</Label>
                    <Input
                      id='name'
                      value={formValues.name}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFieldValue('name', e.currentTarget.value)
                      }
                      disabled={updatingSpace}
                      autoFocus
                    />
                    {formErrors.name && <p className='text-sm text-red-500'>{formErrors.name}</p>}
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='namespace'>Namespace</Label>
                    <Input
                      id='namespace'
                      value={formValues.namespace}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFieldValue('namespace', e.currentTarget.value)
                      }
                      disabled
                      placeholder='n/a'
                    />
                  </div>

                  <Button className='w-full' type='submit' disabled={updatingSpace}>
                    Update {currentApp?.entity}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={openedMoveSpace} onOpenChange={setOpenedMoveSpace}>
            <DialogContent className='sm:max-w-md'>
              <DialogHeader>
                <DialogTitle>
                  Move {space.name} {currentApp?.entity}?
                </DialogTitle>
              </DialogHeader>
              <Text className='mb-4 text-sm'>
                Select an App to move this {currentApp?.entity} there. This action can be undone at any time by simply
                moving it again.
              </Text>
              <div>
                <div className='mb-4 space-y-2'>
                  <Label>Apps</Label>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant='outline' className='w-full flex justify-between items-center'>
                        <span>{selectedApp || 'Select an app'}</span>
                        <Icon name='chevron-down-small' />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className='w-full'>
                      {otherApps.map((app) => (
                        <DropdownMenuItem key={app.id} onClick={() => setSelectedApp(app.name)}>
                          {app.name}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <Button className='w-full mt-6' onClick={handleMoveClick}>
                  Move {currentApp?.entity} {selectedApp ? `to ${selectedApp}` : ''}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={openedPinSpace} onOpenChange={setOpenedPinSpace}>
            <DialogContent className='sm:max-w-md'>
              <DialogHeader>
                <DialogTitle>
                  Link {space.name} {currentApp?.entity} to this App?
                </DialogTitle>
              </DialogHeader>
              <div>
                <Text className='text-sm'>
                  Attaching this {currentApp?.entity} to your <b>{currentApp?.name}</b> App will anchor it in place.
                  This means it will stay within the {currentApp?.name} App, regardless of changing your Default App for
                  this Account.
                </Text>

                <Button className='w-full mt-6' onClick={handlePinClick}>
                  Link {currentApp?.entity} to {currentApp?.name}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant='ghost'
                size='icon-md'
                icon='dot-grid-1x3-vertical'
                iconProps={{ size: 12 }}
                aria-label='More Options'
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem onClick={() => window.open(spaceRootUrl, '_blank', 'noopener,noreferrer')}>
                {`Open ${currentApp?.entity ?? `session`}`} in new tab
              </DropdownMenuItem>
              {currentApp && (
                <>
                  {hasPermission({ resource: environment, permissionKey: 'space:update' }) && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => setOpenedUpdateSpace(true)}>
                        {`Edit ${currentApp.entity}`}
                      </DropdownMenuItem>
                      {isDefault && !space.appId && (
                        <DropdownMenuItem onClick={() => setOpenedPinSpace(true)}>
                          {`Link ${currentApp.entity} to ${currentApp.name}`}
                        </DropdownMenuItem>
                      )}
                      {otherApps.length > 0 && (
                        <DropdownMenuItem onClick={() => setOpenedMoveSpace(true)}>
                          {`Move ${currentApp.entity}`}
                        </DropdownMenuItem>
                      )}

                      <DropdownMenuItem onClick={handleArchive}>
                        {`${space.archivedAt ? 'Unarchive' : 'Archive'} ${currentApp.entity}`}
                      </DropdownMenuItem>

                      <DropdownMenuSeparator />
                    </>
                  )}

                  {hasPermission({ resource: environment, permissionKey: 'space:delete' }) && (
                    <DropdownMenuItem className='text-red-600' onClick={() => setOpenedDeleteSpace(true)}>
                      {`Delete ${currentApp.entity}`}
                    </DropdownMenuItem>
                  )}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </td>
    </tr>
  )
})
CustomAppRow.displayName = 'CustomAppRow'

export const CustomAppListPage = () => {
  const { identity, hasPermission } = useIdentity()
  const { environment, hasEnvAccess } = useActiveEnvironment()
  const canCreateSpace = hasPermission({ resource: environment, permissionKey: 'space:create' })
  const canReadAgents = hasPermission({ resource: environment, permissionKey: 'agent:read' })
  const { id = '' } = useParams()
  const { namespace } = useParams()
  const currentAppPredicate = useCallback((app: App) => app.id === id || app.namespace === namespace, [id, namespace])
  const currentAppId = useFilteredCollection('APPS', currentAppPredicate)
  const currentApp = useResource('APPS', currentAppId[0])
  const apps = useResourceList('APPS').response.data
  const inputRef = useRef<HTMLInputElement | null>(null)
  const otherApps = useMemo(
    () => apps.filter((a: any) => ![currentApp?.name!, 'Mapping'].includes(a.name)),
    [apps, currentApp]
  )

  const resolvedAppId = useMemo(() => {
    if (currentApp?.id) {
      return currentApp.id
    }

    if (id && id.trim() !== '') {
      return id
    }

    return null
  }, [currentApp?.id, id])

  // Get the template spaces for the current app
  const templateSpaces = useCollection('SPACES', {
    app: currentApp?.id ?? '',
    isAppTemplate: true,
    environmentId: environment?.id,
    filterByEnvId: hasEnvAccess,
  })
  // Ensure the template space is actually from the current environment to prevent showing stale data when switching environments
  const templateSpace = useMemo(() => {
    const candidate = templateSpaces[0]
    return candidate && candidate.environmentId === environment?.id ? candidate : undefined
  }, [templateSpaces, environment?.id])

  const location = useLocation()
  const [deploying, setDeploying] = useState(() => {
    // Initialize deploying state from location.state if available
    return location.state?.deploying || false
  })

  const { addToRefsArray, listHeight } = useDynamicHeight()
  const [showArchived, setShowArchived] = useState(false)

  const [currentPage, setCurrentPage] = useState(1)
  const [pageCount, setPageCount] = useState(0)
  const [requestParams, setRequestParams] = useState<RequestParams>({
    app: currentApp?.id ?? '',
    sortField: undefined,
    sortDirection: undefined,
    archived: false,
    isAppTemplate: false,
  })
  const [searchValue, setSearchValue] = useState<string | undefined>()
  const debouncedSearchValue = useDebounce<string | undefined>(searchValue, 300)
  const [resetCache, setResetCache] = useState(0)
  const { statsData } = useCustomAppPage(resolvedAppId, environment?.id)

  const statsUI: StatsUI[] = useMemo(() => (statsData ? convertStatsResponseToUI(statsData) : []), [statsData])

  const pagination: ListPagination = { currentPage, pageCount, setPageCount }
  const [columnWidths, setColumnWidths] = useState<{ [key: string]: number }>({})
  const tableRef = useRef<HTMLTableSectionElement>(null)

  useEffect(() => {
    if (location.state?.deploying) {
      setDeploying(true)
      // do not persist deploying state on page refresh
      window.history.replaceState({}, document.title)
    }
  }, [location.state])

  useEffect(() => {
    if (tableRef.current) {
      const _headers = tableRef.current.querySelectorAll('th')
      const newWidths: { [key: string]: number } = {}

      // Define percentage widths for each column
      newWidths['column0'] = 3 // Checkbox column
      newWidths['column1'] = 7 // ID column
      newWidths['column2'] = 12 // Name column
      newWidths['column3'] = 10 // Labels column
      newWidths['column4'] = 10 // Created by column
      newWidths['column5'] = 10 // Created at column
      newWidths['column6'] = 10 // Last Activity column
      newWidths['column7'] = 7 // Files column
      newWidths['column8'] = 7 // Data column
      newWidths['column9'] = 7 // PDV column
      newWidths['column10'] = 7 // Guests column
      newWidths['column11'] = 5 // Actions column

      setColumnWidths(newWidths)
    }
  }, [])

  useEffect(() => {
    if (debouncedSearchValue || requestParams.app !== currentApp?.id) {
      inputRef?.current?.focus()
      setCurrentPage(1)
    }

    setRequestParams((currParams) => {
      const newParams = { ...currParams }
      if (currentApp?.id) newParams.app = currentApp.id
      if (!debouncedSearchValue && newParams.search) delete newParams.search
      else if (debouncedSearchValue) newParams.search = debouncedSearchValue
      return newParams
    })
  }, [debouncedSearchValue, currentApp?.id])

  useEffect(() => {
    setCurrentPage(1)
  }, [requestParams.sortField, requestParams.sortDirection])

  const onSearch = useCallback((value: string) => {
    if (value) {
      setSearchValue(value)
    } else {
      setSearchValue(undefined)
    }
  }, [])

  const handleSpaceCreated = (newSpace: Space) => {
    const spaceRootUrl = spacesUrl(
      newSpace?.id ?? '',
      identity.token,
      identity.account.vanityDomainSpaces,
      identity.account.subdomain
    )
    setCurrentPage(1)
    setRequestParams({
      app: currentApp?.id ?? '',
      archived: showArchived,
      isAppTemplate: false,
    })
    setResetCache((currCacheKey) => currCacheKey + 1)
    window.open(spaceRootUrl, '_blank')
  }

  const handleOpenTemplateSpace = () => {
    if (templateSpace) {
      // Open template space in assistant mode in the same tab
      const spaceRootUrl = spacesUrl(
        templateSpace.id,
        identity.token,
        identity.account.vanityDomainSpaces,
        identity.account.subdomain,
        'assistant'
      )
      window.location.href = spaceRootUrl
    }
  }

  const handleBulkSuccess = () => {
    setRequestParams({ ...requestParams })
    setResetCache((currCacheKey) => currCacheKey + 1)
  }

  const handleRowActionSuccess = (action: string) => {
    if (['DELETE', 'MOVE', 'PIN', 'ARCHIVE'].includes(action)) {
      setRequestParams({ ...requestParams })
      setResetCache((currCacheKey) => currCacheKey + 1)
    }
  }

  const handleTabClick = (val: string) => {
    const isArchived = val === 'archived'
    setShowArchived(isArchived)
    setRequestParams({ ...requestParams, archived: isArchived })
  }

  const filterType = environment?.isProd ? 'PROD' : 'DEV'

  // Add effect to ensure app ID is updated correctly when currentApp changes
  useEffect(() => {
    if (currentApp?.id) {
      setRequestParams((prev) => ({
        ...prev,
        app: currentApp.id,
        isAppTemplate: false,
      }))
    }
  }, [currentApp?.id])

  return (
    <>
      {(hasEnvAccess || currentApp) && (
        <ResourceListContextProvider requestParams={requestParams} resourceType='SPACES'>
          <div className='w-full h-full flex flex-col relative'>
            <div className='flex justify-between items-center p-3'>
              <Text variant='title2'>{currentApp?.name || 'Spaces'}</Text>
              <div className='flex gap-2'>
                <TrialBanner />
                {templateSpace && (
                  <Button
                    size='md'
                    variant='outline'
                    onClick={handleOpenTemplateSpace}
                    icon='pencil'
                    iconProps={{ size: 16 }}
                  >
                    Edit Template
                  </Button>
                )}
                {canCreateSpace &&
                  (deploying ? (
                    <Button size='md' disabled>
                      Deploying agent...
                    </Button>
                  ) : (
                    <CreateNewSpace
                      entity={currentApp?.entity || 'Session'}
                      onSpaceCreated={handleSpaceCreated}
                      environmentId={environment?.id}
                      isProd={environment?.isProd}
                      disabled={!currentApp?.environmentFilters?.type?.includes(filterType) || deploying}
                      canReadAgents={canReadAgents}
                    />
                  ))}
              </div>
            </div>
            <div ref={addToRefsArray} className='w-full p-3 relative h-full'>
              <div>
                <Tabs
                  defaultValue={showArchived ? 'archived' : 'active'}
                  onValueChange={handleTabClick}
                  className='border-b border-gray-200'
                >
                  <div className='flex justify-between items-center px-2 w-full mt-3'>
                    <TabsList className='bg-transparent p-0 h-full gap-1'>
                      <TabsTrigger
                        value='active'
                        className='px-4 py-2 min-h-[44px] data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none hover:text-primary rounded-none'
                      >
                        <Text variant='body' emphasized={!showArchived} className={!showArchived ? '' : 'text-muted'}>
                          Active {currentApp?.entityPlural || 'Sessions'}
                        </Text>
                      </TabsTrigger>
                      <TabsTrigger
                        value='archived'
                        className='px-4 py-2 min-h-[44px] data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none hover:text-primary rounded-none'
                      >
                        <Text variant='body' emphasized={showArchived} className={showArchived ? '' : 'text-muted'}>
                          Archived {currentApp?.entityPlural || 'Sessions'}
                        </Text>
                      </TabsTrigger>
                    </TabsList>
                  </div>
                </Tabs>
              </div>
              {currentApp?.type === AppType.Portal && (
                <>
                  <div className='w-full min-w-[320px]'>
                    {hasEnvAccess &&
                      (statsUI.length > 0 ? (
                        <StatsGrid data={statsUI} />
                      ) : (
                        <>
                          <div className='flex items-center justify-center h-[195px] text-gray-500 text-sm'>
                            <em>stats will display here once there's data</em>
                          </div>
                        </>
                      ))}
                  </div>
                </>
              )}
              <div>
                <div className='flex justify-between items-center mx-2 py-4'>
                  <div className='relative flex-1'>
                    <SimpleSearchBar
                      ref={inputRef}
                      onSearchChange={onSearch}
                      placeholder='Search by id, name or label...'
                      searchTerm={searchValue || ''}
                      className='min-w-[250px] max-w-md'
                      size='sm'
                    />
                  </div>

                  <div className='flex justify-end items-center'>
                    <div className='mr-4'>
                      <ResourceListBulkActions>
                        {hasPermission({ resource: environment, permissionKey: 'space:delete' }) && (
                          <ResourceListBulkActions.DeleteAction
                            entity={currentApp?.entity}
                            entityPlural={currentApp?.entityPlural}
                            resourceType='SPACES'
                            onBulkActionComplete={handleBulkSuccess}
                          />
                        )}
                        {hasPermission({ resource: environment, permissionKey: 'space:update' }) &&
                          otherApps.length > 0 && (
                            <ResourceListBulkActions.MoveAction
                              entity={currentApp?.entity}
                              entityPlural={currentApp?.entityPlural}
                              params={{ app: currentApp! }}
                              resourceType='SPACES'
                              onBulkActionComplete={handleBulkSuccess}
                            />
                          )}
                        {hasPermission({ resource: environment, permissionKey: 'space:update' }) && (
                          <ResourceListBulkActions.ArchiveAction
                            entity={currentApp?.entity}
                            entityPlural={currentApp?.entityPlural}
                            params={{ action: showArchived ? 'unarchive' : 'archive' }}
                            resourceType='SPACES'
                            onBulkActionComplete={handleBulkSuccess}
                          />
                        )}
                      </ResourceListBulkActions>
                    </div>

                    <Pagination className='w-fit' iconOnly size='sm'>
                      <PaginationContent>
                        <PaginationPrevious
                          onClick={() => {
                            if (currentPage > 1) setCurrentPage(currentPage - 1)
                          }}
                          disabled={currentPage <= 1}
                        />
                        {(() => {
                          const maxVisiblePages = 5
                          let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
                          // Always show at least one page
                          const effectivePageCount = Math.max(1, pagination.pageCount)
                          const endPage = Math.min(effectivePageCount, startPage + maxVisiblePages - 1)

                          // Adjust start page if we're near the end
                          if (endPage - startPage + 1 < maxVisiblePages) {
                            startPage = Math.max(1, endPage - maxVisiblePages + 1)
                          }

                          // Generate array of page numbers to display
                          const pages = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i)

                          return pages.map((pageNum) => (
                            <PaginationItem key={pageNum}>
                              <PaginationLink
                                isActive={pageNum === currentPage}
                                onClick={() => setCurrentPage(pageNum)}
                                className={cn(
                                  'w-5 h-5 flex items-center justify-center rounded-full p-0',
                                  pageNum === currentPage
                                    ? 'bg-surface-sentiment-subtle-neutral border-none'
                                    : 'hover:bg-gray-100'
                                )}
                              >
                                {pageNum}
                              </PaginationLink>
                            </PaginationItem>
                          ))
                        })()}
                        <PaginationNext
                          onClick={() => {
                            if (currentPage < pagination.pageCount) setCurrentPage(currentPage + 1)
                          }}
                          disabled={currentPage >= pagination.pageCount || pagination.pageCount <= 1}
                        />
                      </PaginationContent>
                    </Pagination>
                  </div>
                </div>
              </div>
              <div className='relative'>
                <div className='overflow-x-auto h-full relative'>
                  <div className='min-w-[1100px] h-full'>
                    <ResourceList
                      height={listHeight}
                      errorMessage={`Unable to load ${currentApp?.entityPlural || 'Session'}`}
                      emptyMessage={
                        showArchived
                          ? `No archived ${currentApp?.entityPlural || 'Sessions'}`
                          : `Waiting for your first ${currentApp?.entity || 'Session'}...`
                      }
                      headerComponent={CustomAppTableHeaderGenerator({
                        requestParams,
                        setRequestParams,
                        columnWidths,
                        tableRef,
                      })}
                      rowComponent={CustomAppRow}
                      onRowActionSuccess={handleRowActionSuccess}
                      pagination={pagination}
                      forceMount={resetCache}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ResourceListContextProvider>
      )}
      {!hasEnvAccess && !currentApp && (
        <CustomErrorComponent
          title='App Not Found'
          message="The requested app could not be found or you don't have access to it."
          page='CustomAppList'
        />
      )}
    </>
  )
}
export default CustomAppListPage
